import {
  computed, makeObservable, observable, runInAction,
} from 'mobx';
import { nanoid } from 'nanoid';
import { Consent, ConsentGroup } from '@mfe-4cs/types/comsApi';
import Service from '@mfe-4cs/util/Service';
import { TefComs } from '@mfe-4cs/types/typings';
import { ConsentStore } from './ConsentStore';

export class ConsentGroupStore implements ConsentGroup {
  private $id: string;

  changes: number = 0;
  consents: ConsentStore[] = [];
  consentsOrigin: Consent[] = [];
  denyall?: string;
  denyallView?: string;
  footer?: string;
  header?: string;
  name?: string;
  oneclick?: boolean;
  oneclickCollapsible?: string;
  required?: boolean;
  text?: string;

  private service;
  collapsibleOpen = false;

  constructor(data: ConsentGroup, fromStorage = false) {
    makeObservable(this, {
      changes: observable,
      consents: observable,
      areAllConsentsDenied: computed,
      areAllConsentsAndFeaturesGranted: computed,
      collapsibleOpen: observable,
    });
    const { consents, ...restData } = data;
    Object.assign(this, restData);
    this.service = new Service();
    this.consents = consents.map((c) => new ConsentStore(c));
    this.$id = nanoid();
    this.name = this.name ?? 'noname';
    if (this.required && !fromStorage) {
      this.consents.forEach((consent) => {
        consent.setGrantedWithFeatures(true);
      });
    }
    if (!fromStorage) {
      this.consentsOrigin = consents;
    }
  }

  oneClickToggle(e: PointerEvent): void {
    e.preventDefault(); // prevent double triggering
    const currentState = this.areAllConsentsAndFeaturesGranted;
    this.consents.forEach((consent) => {
      consent.setGrantedWithFeatures(!currentState);
      consent.setModified(true);
    });
    this.triggerChange();
  }

  oneClickToggleOCCL(): void {
    const currentState = this.areAllConsentsAndFeaturesGranted;
    this.consents.forEach((consent) => {
      consent.setGrantedWithFeatures(!currentState);
      consent.setModified(true);
    });
    this.triggerChange();
  }

  denyAll(e: PointerEvent): void {
    e.preventDefault(); // prevent double triggering
    if (!this.areAllConsentsDenied) {
      this.consents.forEach((consent) => {
        consent.setGrantedWithFeatures(false);
        consent.setModified(true);
      });
    } else {
      this.consents = this.consentsOrigin.map((c) => new ConsentStore(c));
    }
    this.triggerChange();
  }

  denyAllOCCL(): void {
    if (!this.areAllConsentsDenied) {
      this.consents.forEach((consent) => {
        consent.setGrantedWithFeatures(false);
        consent.setModified(true);
      });
    } else {
      this.consents = this.consentsOrigin.map((c) => new ConsentStore(c));
    }
    this.triggerChange();
  }

  get areAllConsentsDenied(): boolean {
    return !this.consents.find((c) => c.granted);
  }

  get areAllConsentsAndFeaturesGranted(): boolean {
    return !this.consents.find((c) => !c.granted || c.hasNotGrantedFeature);
  }

  get id(): string {
    return this.$id;
  }

  public toJSON(): ConsentGroup {
    return {
      ...this,
      consents: this.consents.map((cs) => cs.toJSON()),
    };
  }

  public triggerChange(): void {
    runInAction(() => {
      this.changes += 1;
    });
    this.service.fireEvent<TefComs.EventDetails>({
      name: 'consents/CHANGE',
      level: 'info',
      message: 'user changed a consent',
    });
  }
}
